import React, { useState } from 'react';
import { ManageColumns } from './ManageColumns';
import {
    BarsArrowDownIcon,
    BarsArrowUpIcon,
    CogIcon,
    EllipsisVerticalIcon,
    EyeSlashIcon,
    FunnelIcon,
} from '@heroicons/react/24/outline';
import { SortDirection } from '@src/types/common';
import { ArrowsUpDownIcon } from '@heroicons/react/24/solid';
import { Dropdown } from 'antd';
import { useMediaQuery } from '@src/hooks';
import { useTheme } from '@src/theme';

interface ColumnHeaderWithMenuProps {
    title: string;
    columnKey: string;
    allColumns: { key: string; title: string }[];
    hiddenColumns: string[];
    sortable?: boolean;
    sortOrder?: SortDirection | null;
    onSortChange?: (order: SortDirection | null) => void;
    setHiddenColumns: (keys: string[]) => void;
    onColumnMenuClick: (colKey: string, action: string) => void;
}

export const ColumnHeaderWithMenu: React.FC<ColumnHeaderWithMenuProps> = ({
    title,
    columnKey,
    allColumns,
    hiddenColumns,
    sortable = false,
    sortOrder,
    setHiddenColumns,
    onColumnMenuClick,
    onSortChange,
}) => {
    const { theme } = useTheme();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showManage, setShowManage] = useState(false);

    const handleDropdownVisibleChange = (open: boolean) => {
        setDropdownOpen(open);
        if (!open) {
            setShowManage(false);
        }
    };

    const handleSortClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!sortable || !onSortChange) return;

        let nextSort: SortDirection | null = null;
        switch (sortOrder) {
            case null:
                nextSort = 'ASC';
                break;
            case 'ASC':
                nextSort = 'DESC';
                break;
            case 'DESC':
                nextSort = null;
                break;
        }
        onSortChange(nextSort);
    };

    const baseMenuItems = [
        ...(sortable
            ? [
                  {
                      key: 'sortAsc',
                      icon: <BarsArrowUpIcon width={16} height={16} />,
                      label: 'Sort by ASC',
                  },
                  {
                      key: 'sortDesc',
                      icon: <BarsArrowDownIcon width={16} height={16} />,
                      label: 'Sort by DESC',
                  },
                  { type: 'divider' as const },
              ]
            : []),

        {
            key: 'filter',
            icon: <FunnelIcon width={16} height={16} />,
            label: 'Filter',
        },
        { type: 'divider' as const },
        {
            key: 'hide',
            icon: <EyeSlashIcon width={16} height={16} />,
            label: 'Hide Column',
        },
        {
            key: 'manageColumns',
            icon: <CogIcon width={16} height={16} />,
            label: 'Manage Columns',
        },
    ];
    const isMobile = useMediaQuery('(max-width: 767px)');

    return (
        <div
            className="flex items-center justify-between !w-full"
            onClick={(e) => e.stopPropagation()}
        >
            <span
                className={`flex items-center gap-1 ${sortable ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={sortable ? handleSortClick : undefined}
            >
                {sortable &&
                    (sortOrder === 'ASC' ? (
                        <BarsArrowUpIcon width={16} height={16} />
                    ) : sortOrder === 'DESC' ? (
                        <BarsArrowDownIcon width={16} height={16} />
                    ) : (
                        <ArrowsUpDownIcon
                            width={16}
                            height={16}
                            className="text-gray-400"
                        />
                    ))}
                {title}
            </span>

            <Dropdown
                trigger={['click']}
                placement={isMobile ? 'bottom' : 'bottomRight'}
                open={dropdownOpen}
                onOpenChange={handleDropdownVisibleChange}
                dropdownRender={() =>
                    showManage ? (
                        <div className="rounded-xl">
                            <ManageColumns
                                allColumns={allColumns}
                                hiddenColumns={hiddenColumns}
                                setHiddenColumns={setHiddenColumns}
                                onClose={() => setShowManage(false)}
                            />
                        </div>
                    ) : (
                        <div className="p-2 min-w-[180px] rounded-xl shadow-2xl bg-surface">
                            {baseMenuItems.map((item, idx) =>
                                item.type === 'divider' ? (
                                    <hr
                                        key={`divider-${idx}`}
                                        className="my-2"
                                    />
                                ) : (
                                    <div
                                        key={item.key}
                                        className="flex items-center gap-2 py-1 px-2 cursor-pointer rounded hover:bg-background"
                                        style={{
                                            color: theme.semantic.textPrimary,
                                        }}
                                        onClick={() => {
                                            if (item.key === 'manageColumns') {
                                                setShowManage(true);
                                            } else {
                                                setDropdownOpen(false);
                                                onColumnMenuClick(
                                                    columnKey,
                                                    item.key
                                                );
                                            }
                                        }}
                                    >
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </div>
                                )
                            )}
                        </div>
                    )
                }
            >
                <EllipsisVerticalIcon
                    width={20}
                    height={20}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => e.stopPropagation()}
                />
            </Dropdown>
        </div>
    );
};
